import { BrowserModule } from '@angular/platform-browser';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { APP_CONFIG, AppConfig } from './app.config';
import { registerLocaleData } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideTranslateLoader, provideTranslateService, TranslateLoader, TranslatePipe } from '@ngx-translate/core';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { RentModule } from './rent/rent.module';
import { MessageModule } from './message/message.module';
import { OverviewModule } from './overview/overview.module';

import { AppComponent } from './app.component';
import { PageNotFoundComponent } from './root/component/page-not-found/page-not-found.component';
import { CookieInfoComponent } from './root/component/cookie-info/cookie-info.component';

import localeEnGb from '@angular/common/locales/en';
import localeFi from '@angular/common/locales/fi';
import localeSv from '@angular/common/locales/sv';
import localeDa from '@angular/common/locales/da';
import { DashboardModule } from './dashboard/dashboard.module';
import { MatNativeDateModule } from '@angular/material/core';
import { MessageService } from './core/service/message.service';
import { ProgressBarService } from './core/service/progress-bar.service';
import { UserDialogService } from './core/service/user-dialog.service';
import { ActionWindowService } from './core/service/action-window.service';
import { NotificationBarService } from './core/service/notification-bar.service';
import { ReportModule } from './report/report.module';
import { CommonModule } from './common/common.module';
import { ConsumptionModule } from './consumption/consumption.module';
import { LettingModule } from './letting/letting.module';
import { NewDashboardItemComponent } from './dashboard/component/new-dashboard-item/new-dashboard-item.component';
import { MortgageModule } from './mortgage/mortgage.module';
import { SnackbarService } from './core/service/snackbar.service';
import { DebugModule } from './_debug/debug.module';
import { firstValueFrom, from, Observable } from 'rxjs';
import { LanguageService } from './core/service/language.service';

registerLocaleData(localeEnGb, 'en');
registerLocaleData(localeFi, 'fi');
registerLocaleData(localeSv, 'sv');
registerLocaleData(localeDa, 'da');

class LazyTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<any> {
    // esbuild/application builder returns dynamic JSON imports as a module namespace
    // ({ default: {...} }); webpack returned the object directly. Unwrap to support both.
    return from(import(`../assets/i18n/${lang}.json`).then((m: any) => m.default ?? m));
  }
}

@NgModule({ declarations: [AppComponent, CookieInfoComponent, PageNotFoundComponent, NewDashboardItemComponent],
    exports: [],
    bootstrap: [AppComponent], imports: [DashboardModule,
        BrowserModule,
        BrowserAnimationsModule,
        ConsumptionModule,
        CoreModule,
        CommonModule,
        LettingModule,
        MatNativeDateModule,
        RentModule,
        MessageModule,
        MortgageModule,
        OverviewModule,
        ReportModule,
        AppRoutingModule,
        TranslatePipe,
        DebugModule], providers: [
        ActionWindowService,
        MessageService,
        NotificationBarService,
        ProgressBarService,
        UserDialogService,
        SnackbarService,
        { provide: APP_CONFIG, useClass: AppConfig },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideTranslateService({
            lang: 'sv',
            fallbackLang: 'sv',
            loader: provideTranslateLoader(LazyTranslateLoader),
        }),
        provideAppInitializer(() => firstValueFrom(inject(LanguageService).initialize())),
    ] })
export class AppModule {}
